import { useEffect, useState } from "react";
import { FileText, Plus, Download, ExternalLink, Calendar, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_PILL = {
  clean: { bg: "bg-emerald-100", text: "text-emerald-800", label: "No issues" },
  issues: { bg: "bg-amber-100", text: "text-amber-800", label: "Issues identified" },
};

export default function TradeCertificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [whiteLabel, setWhiteLabel] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/conveyancing/certificates")
      .then(({ data }) => setCerts(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/trade/white-label").then(({ data }) => setWhiteLabel(data?.brand_name ? data : null)).catch(() => {});
  }, []);

  const downloadPdf = async (certId, certNumber) => {
    const token = localStorage.getItem("scwt_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conveyancing/certificates/${certId}/download.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `conveyancing-water-certificate-${certNumber}.pdf`;
    a.click();
  };

  return (
    <PortalShell title="Conveyancing certificates" subtitle="Generate, download and audit Conveyancing Water Certificates™ for properties you've serviced.">
      <div data-testid="trade-certs-page">
        <div className="flex items-center justify-between mb-5">
          <div className="text-sm text-slate-600">
            {whiteLabel ? (
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Branded as <strong>{whiteLabel.brand_name}</strong></span>
            ) : (
              <span className="text-slate-500">No white-label configured — certificates render with Aware branding. <a href="/trade/branding" className="text-cyan-700 hover:underline font-semibold">Configure branding →</a></span>
            )}
          </div>
          <Button data-testid="trade-cert-new-btn" onClick={() => setShowGenerate(true)} className="btn-aqua rounded-full font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> Generate certificate
          </Button>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">Certificate</th>
                  <th className="text-left px-5 py-3">Property</th>
                  <th className="text-left px-5 py-3">Score</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Issued</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-12 text-slate-500">Loading...</td></tr>
                ) : certs.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-12 text-slate-500">
                    <FileText className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                    No certificates yet. Generate your first one — by AWARE Water Health Record ID.
                  </td></tr>
                ) : (
                  certs.map((c) => {
                    const pill = STATUS_PILL[c.snapshot?.status] || STATUS_PILL.clean;
                    return (
                      <tr key={c.id} data-testid={`cert-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                        <td className="px-5 py-4">
                          <div className="font-mono text-xs text-slate-900 font-semibold">{c.cert_number}</div>
                          {c.white_label_applied && <div className="mt-0.5 text-[9px] uppercase tracking-wider text-cyan-700 font-bold">White-labelled</div>}
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-slate-900">{c.property_address || "—"}</div>
                        </td>
                        <td className="px-5 py-4 font-bold text-slate-900">{c.snapshot?.health_score ?? "—"}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${pill.bg} ${pill.text}`}>{pill.label}</span>
                        </td>
                        <td className="px-5 py-4 text-slate-700 text-xs">
                          {formatDateAU(c.generated_at)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="inline-flex gap-1.5">
                            <button data-testid={`cert-download-${c.id}`} onClick={() => downloadPdf(c.id, c.cert_number)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 hover:bg-cyan-700 text-white text-xs font-semibold">
                              <Download className="w-3.5 h-3.5" /> PDF
                            </button>
                            <a href={c.verification_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-cyan-400 hover:text-cyan-700 text-slate-700 text-xs font-semibold">
                              <ExternalLink className="w-3.5 h-3.5" /> Verify
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showGenerate && <GenerateModal onClose={() => { setShowGenerate(false); load(); }} hasWhiteLabel={!!whiteLabel} />}
    </PortalShell>
  );
}


function GenerateModal({ onClose, hasWhiteLabel }) {
  const [propertyId, setPropertyId] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [applyBranding, setApplyBranding] = useState(hasWhiteLabel);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!propertyId) { toast.error("Please enter the AWARE Water Health Record ID"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/conveyancing/certificates", {
        property_id: propertyId.trim(),
        apply_white_label: applyBranding,
        passport_share_url: shareUrl || null,
        public_base_url: window.location.origin,
      });
      setCreated(data);
      toast.success("Certificate generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally { setSubmitting(false); }
  };

  const downloadPdf = async (certId, certNumber) => {
    const token = localStorage.getItem("scwt_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conveyancing/certificates/${certId}/download.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `conveyancing-water-certificate-${certNumber}.pdf`; a.click();
  };

  return (
    <div data-testid="trade-cert-modal" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-7 my-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">Conveyancing Water Certificate™</div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">Generate certificate</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        {!created && (
          <div className="mt-5 space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-700">AWARE Water Health Record ID</Label>
              <Input data-testid="cert-property-id" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} placeholder="e.g. de6aaf44-…" className="mt-1.5 font-mono text-sm" />
              <p className="text-[11px] text-slate-500 mt-1.5">Ask the property owner for the Record ID from their /passport dashboard.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Active share link (optional)</Label>
              <Input data-testid="cert-share-url" type="url" value={shareUrl} onChange={(e) => setShareUrl(e.target.value)} placeholder="https://… property share link" className="mt-1.5" />
            </div>
            {hasWhiteLabel && (
              <label className="flex items-center gap-2.5 text-sm text-slate-700">
                <input data-testid="cert-apply-branding" type="checkbox" checked={applyBranding} onChange={(e) => setApplyBranding(e.target.checked)} className="rounded border-slate-300" />
                Apply my white-label branding
              </label>
            )}
            <Button data-testid="cert-submit" onClick={submit} disabled={submitting || !propertyId} className="btn-aqua rounded-full font-semibold w-full">
              {submitting ? "Generating..." : "Generate certificate"}
            </Button>
          </div>
        )}

        {created && (
          <div data-testid="cert-created" className="mt-5 space-y-4">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
              <div className="text-sm font-semibold text-emerald-900">Certificate ready</div>
              <div className="font-mono text-xs text-emerald-800 mt-1">{created.cert_number}</div>
            </div>
            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between"><dt className="text-slate-500">Property</dt><dd className="text-slate-900 text-right">{created.snapshot.address}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Score</dt><dd className="text-slate-900 font-bold">{created.snapshot.health_score}/100</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Status</dt><dd className={`font-semibold ${created.snapshot.status === "clean" ? "text-emerald-700" : "text-amber-700"}`}>{created.snapshot.status === "clean" ? "No issues" : "Issues"}</dd></div>
            </dl>
            <Button onClick={() => downloadPdf(created.id, created.cert_number)} className="btn-aqua rounded-full font-semibold w-full">
              <Download className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
            <Button variant="outline" onClick={onClose} className="rounded-full w-full">Done</Button>
          </div>
        )}
      </div>
    </div>
  );
}
